import { gpHome } from '../../app';
import {
    AnalysisProject,
    AnalysisProjectBackgroundTask,
    AnalysisProjectServiceName,
    AnalysisProjectProcessingServiceName,
    IAnalysisProjectService,
    IAnalysisProjectProcessingService,
    Report,
} from '../../services/analysis-contract';
import {
    IStorageProviderFactory,
    StorageProviderFactoryServiceName,
} from '../../../app-common/services/storage-provider-contract';
import { IRouteAuthService, IRouteAuthServiceEvents } from '../../services/route-auth-service';
import analyzeDetailTemplate from './analyze-detail.html';

class AnalyzeDetailComponent {

    static $inject = [
        AnalysisProjectServiceName,
        AnalysisProjectProcessingServiceName,
        StorageProviderFactoryServiceName,
        '$routeParams',
        '$location',
        '$interval',
        'gpRouteAuthService',
    ];

    project: AnalysisProject | null = null;
    selectedTask: AnalysisProjectBackgroundTask | null = null;
    isLoading = true;
    isConverting = false;
    errorMessage: string | null = null;

    private pollHandle: ng.IPromise<void> | null = null;

    private readonly authEvents: IRouteAuthServiceEvents = {
        loggedIn: () => { this.loadProject(); },
        loggedOut: () => { this.$location.path('/login'); },
    };

    constructor(
        private readonly analysisProjectService: IAnalysisProjectService,
        private readonly processingService: IAnalysisProjectProcessingService,
        private readonly storageFactory: IStorageProviderFactory,
        private readonly $routeParams: ng.route.IRouteParamsService,
        private readonly $location: ng.ILocationService,
        private readonly $interval: ng.IIntervalService,
        private readonly gpRouteAuthService: IRouteAuthService,
    ) { }

    $onInit(): void {
        this.gpRouteAuthService.register(this.authEvents);
        this.loadProject();
    }

    $onDestroy(): void {
        this.gpRouteAuthService.deregister(this.authEvents);
        this.stopPolling();
    }

    selectTask(task: AnalysisProjectBackgroundTask): void {
        this.selectedTask = (this.selectedTask && this.selectedTask.id === task.id) ? null : task;
    }

    goBack(): void {
        this.$location.path('/analyze');
    }

    getActiveReport(): Report | null {
        if (!this.project || !this.project.reports) return null;
        return this.project.reports.length > 0 ? this.project.reports[0] : null;
    }

    hasAnyRunningTask(): boolean {
        if (!this.project) return false;
        return this.project.tasks.some(t => t.status === 'running' || t.status === 'pending');
    }

    getRunningTask(): AnalysisProjectBackgroundTask | null {
        if (!this.project) return null;
        return this.project.tasks.find(t => t.status === 'running') || null;
    }

    isReportTaskActive(): boolean {
        if (!this.project) return false;
        const task2 = this.project.tasks.find(t => t.name === 'Generating Risk Report');
        return !!(task2 && (task2.status === 'running' || task2.status === 'complete'));
    }

    canGenerateReport(): boolean {
        if (!this.project) return false;
        const task1 = this.project.tasks.find(t => t.name === 'Initial Processing');
        const task2 = this.project.tasks.find(t => t.name === 'Generating Risk Report');
        return !!(task1 && task1.status === 'complete' && task2 && task2.status !== 'running');
    }

    generateReport(): void {
        if (!this.project || !this.canGenerateReport()) return;

        // Pass the project context to the processing service
        const svc = this.processingService as any;
        if (typeof svc.setProjectContext === 'function') {
            svc.setProjectContext(this.project.id, this.project.projectContext || '');
        }
        if (typeof svc.startRiskAnalysis === 'function') {
            svc.startRiskAnalysis(this.project.id);

            // Refresh project immediately so we pick up the running task state,
            // then start polling for ongoing updates.
            const projectId = this.$routeParams['analysisProjectId'];
            this.analysisProjectService.getProject(projectId).then(project => {
                this.project = project;
                this.startPollingIfNeeded();
            });
        }
    }

    formatTaskMessage(text: string | null): string {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br>');
    }

    formatFileSize(): string {
        const bytes = this.project?.fileSizeBytes || 0;
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    private loadProject(): void {
        const projectId = this.$routeParams['analysisProjectId'];
        if (!projectId) {
            this.errorMessage = 'No project ID specified.';
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.analysisProjectService.getProject(projectId).then(project => {
            this.project = project;
            this.isLoading = false;
            this.checkConversionAndInit(project);
            this.startPollingIfNeeded();
        }).catch(() => {
            this.errorMessage = 'Analysis project not found.';
            this.isLoading = false;
        });
    }

    /**
     * Check whether the uploaded project file has been converted yet.
     * Conversion is triggered automatically by a blob trigger when the
     * source file is uploaded — we just poll metadata.json until the
     * status changes to "imported" or "error".
     */
    private checkConversionAndInit(project: AnalysisProject): void {
        // Already initialised in processing service (e.g. just uploaded this session)?
        const existingTasks = this.processingService.getTaskStatus(project.id);
        if (existingTasks.length > 0) return;

        if (project.status === 'imported') {
            this.loadConvertedData(project);
        } else if (project.status === 'error') {
            this.errorMessage = 'Project conversion failed' + (project.errorMessage ? ': ' + project.errorMessage : '.');
        } else {
            // Status is "uploaded" or similar — conversion is pending/in-progress
            this.isConverting = true;
            this.pollForConversion(project);
        }
    }

    private loadConvertedData(project: AnalysisProject): void {
        const sp = this.storageFactory.getProvider('analysis-projects/' + project.id);
        sp.read('10_imported-project.json').then(buffer => {
            const importedProject = JSON.parse(new TextDecoder().decode(buffer));
            this.processingService.initFromImport(project.id, importedProject, sp);

            // Restore any previously saved reports into the processing state
            if (project.reports && project.reports.length > 0) {
                this.processingService.restoreSavedReports(project.id, project.reports);
            }

            this.analysisProjectService.getProject(project.id).then(p => {
                this.project = p;
                this.startPollingIfNeeded();
            });
        });
    }

    /**
     * Manually trigger conversion via the server-side convert endpoint.
     * This calls the same ProjectFileConverter as the blob trigger, but
     * via an HTTP request — useful for retrying after errors.
     */
    retryConversion(): void {
        if (!this.project) return;
        this.errorMessage = null;
        this.isConverting = true;

        // The service seam owns the /convert URL and the credit-gate 402
        // mapping (#74) — string rejections are user-facing messages.
        this.analysisProjectService.retryConversion(this.project.id).then(() => {
            // Conversion succeeded synchronously — reload the project
            this.isConverting = false;
            this.loadConvertedData(this.project!);
        }).catch((err: any) => {
            this.isConverting = false;
            if (typeof err === 'string') {
                this.errorMessage = err;
                return;
            }
            const msg = (err?.data?.error) || err?.statusText || 'Conversion failed';
            this.errorMessage = 'Failed to convert project file: ' + msg;
        });
    }

    /**
     * Poll metadata.json until the convert orchestrator updates the status
     * to "imported" (success) or "error" (failure). Bounded: a conversion
     * that never started (e.g. /convert was refused) would otherwise keep
     * isConverting=true forever, leaving the retry button permanently
     * disabled.
     */
    private pollForConversion(project: AnalysisProject): void {
        const sp = this.storageFactory.getProvider('analysis-projects/' + project.id);
        let attempts = 0;
        const maxAttempts = 150; // ~5 minutes at 2s
        const handle = this.$interval(() => {
            if (++attempts > maxAttempts) {
                this.$interval.cancel(handle);
                this.isConverting = false;
                this.errorMessage = 'Conversion is taking longer than expected. Use "Re-process project file" to try again.';
                return;
            }
            sp.read('metadata.json').then(buffer => {
                const meta = JSON.parse(new TextDecoder().decode(buffer));
                if (meta.status === 'imported') {
                    this.$interval.cancel(handle);
                    this.isConverting = false;
                    this.loadConvertedData(project);
                } else if (meta.status === 'error') {
                    this.$interval.cancel(handle);
                    this.isConverting = false;
                    this.errorMessage = 'Project conversion failed' + (meta.error ? ': ' + meta.error : '.');
                }
            });
        }, 2000);
    }

    private startPollingIfNeeded(): void {
        if (this.pollHandle) return;
        if (!this.hasAnyRunningTask()) return;

        const projectId = this.$routeParams['analysisProjectId'];
        this.pollHandle = this.$interval(() => {
            this.analysisProjectService.getProject(projectId).then(project => {
                this.project = project;

                // Update selected task reference
                if (this.selectedTask && project.tasks) {
                    const updated = project.tasks.find(t => t.id === this.selectedTask!.id);
                    if (updated) {
                        this.selectedTask = updated;
                    }
                }

                if (!this.hasAnyRunningTask()) {
                    this.stopPolling();
                }
            });
        }, 250);
    }

    private stopPolling(): void {
        if (this.pollHandle) {
            this.$interval.cancel(this.pollHandle);
            this.pollHandle = null;
        }
    }
}

gpHome.component('gpAnalyzeDetail', {
    template: analyzeDetailTemplate,
    controller: AnalyzeDetailComponent,
    bindings: {},
});
